/**
 * 取込のバイト供給。File / Blob / ReadableStream / 文字列を同じ `AsyncIterable<Uint8Array>` に揃える。
 * 染色体級を一度にメモリへ載せないため、既定 4 MB ずつ切り出す。
 */

export const DEFAULT_READ_BYTES = 4 * 1024 * 1024;

export async function* iterateBlobBytes(
  blob: Blob,
  readBytes: number = DEFAULT_READ_BYTES
): AsyncGenerator<Uint8Array> {
  for (let offset = 0; offset < blob.size; offset += readBytes) {
    const slice = blob.slice(offset, Math.min(offset + readBytes, blob.size));
    yield new Uint8Array(await slice.arrayBuffer());
  }
}

export async function* iterateReadableStream(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<Uint8Array> {
  const reader = stream.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value && value.length > 0) yield value;
    }
  } finally {
    reader.releaseLock();
  }
}

/** テスト・貼り付け用。任意のバイト幅に刻んでストリームを模す。 */
export async function* iterateTextBytes(
  text: string,
  readBytes: number = DEFAULT_READ_BYTES
): AsyncGenerator<Uint8Array> {
  const bytes = new TextEncoder().encode(text);
  for (let offset = 0; offset < bytes.length; offset += readBytes) {
    yield bytes.subarray(offset, Math.min(offset + readBytes, bytes.length));
  }
}

/** 行単位に切り出す（改行は含めない）。チャンク境界をまたぐ行は連結して返す。 */
export async function* iterateLineBytes(
  source: AsyncIterable<Uint8Array>
): AsyncGenerator<Uint8Array> {
  let carry: Uint8Array | null = null;

  for await (const chunk of source) {
    let start = 0;
    for (let i = 0; i < chunk.length; i += 1) {
      if (chunk[i] !== 0x0a) continue;
      let line: Uint8Array = chunk.subarray(start, i);
      if (carry) {
        const merged: Uint8Array = new Uint8Array(carry.length + line.length);
        merged.set(carry, 0);
        merged.set(line, carry.length);
        line = merged;
        carry = null;
      }
      yield stripCarriageReturn(line);
      start = i + 1;
    }
    if (start < chunk.length) {
      const tail: Uint8Array = chunk.subarray(start);
      if (carry) {
        const merged: Uint8Array = new Uint8Array(carry.length + tail.length);
        merged.set(carry, 0);
        merged.set(tail, carry.length);
        carry = merged;
      } else {
        // 呼び出し側のバッファ再利用に備えて写す
        carry = tail.slice();
      }
    }
  }

  if (carry && carry.length > 0) yield stripCarriageReturn(carry);
}

function stripCarriageReturn(line: Uint8Array): Uint8Array {
  return line.length > 0 && line[line.length - 1] === 0x0d ? line.subarray(0, line.length - 1) : line;
}
